/**
 * Distance-based audio falloff calculations for Counter Pump.
 *
 * CS:GO-style linear distance falloff:
 * - Full volume within minDist (2m)
 * - Linear falloff from minDist to maxDist
 * - Silent beyond maxDist
 * - Footsteps max range: 20m, gunfire/explosions: 40m
 */

/** Minimum distance for full volume (meters). */
const MIN_DIST = 2;

/** Max audible distance for footstep-type sounds (meters). */
const FOOTSTEP_MAX_DIST = 20;

/** Max audible distance for gunfire / explosion / grenade sounds (meters). */
const GUNFIRE_MAX_DIST = 40;

/** Sound names that use the shorter footstep range. */
const FOOTSTEP_SOUNDS = ['footstep', 'jump', 'land'];

export interface DistanceFalloffResult {
  /** Volume multiplier in [0, 1]. */
  factor: number;
}

/**
 * Determine if a sound name should use the short (footstep) max distance.
 */
function isFootstepRange(soundName: string): boolean {
  for (const prefix of FOOTSTEP_SOUNDS) {
    if (soundName === prefix || soundName.startsWith(prefix + '_')) {
      return true;
    }
  }
  return false;
}

/**
 * Compute the distance-based volume falloff factor for a sound.
 *
 * @param distance - Distance from listener to sound source in meters.
 * @param soundName - The registered sound name (used to determine max range).
 * @returns An object with the volume `factor` (0..1).
 */
export function computeDistanceFalloff(
  distance: number,
  soundName: string,
): DistanceFalloffResult {
  if (!Number.isFinite(distance) || distance < 0) return { factor: 0 };

  const maxDist = isFootstepRange(soundName) ? FOOTSTEP_MAX_DIST : GUNFIRE_MAX_DIST;

  let factor: number;

  if (distance <= MIN_DIST) {
    factor = 1.0;
  } else if (distance >= maxDist) {
    factor = 0.0;
  } else {
    // Linear falloff between minDist and maxDist
    factor = 1.0 - (distance - MIN_DIST) / (maxDist - MIN_DIST);
  }

  return { factor };
}

/**
 * Calculate Euclidean distance between two 3D points.
 */
export function distance3D(
  a: { x: number; y: number; z: number },
  b: { x: number; y: number; z: number },
): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  const dz = a.z - b.z;
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}
